import { useEffect } from 'react';

interface ClampPageOptions {
  page: number;
  pageSize: number;
  /** Total row count for the current page request, undefined while it is loading or a placeholder. */
  totalCount: number | undefined;
  setPage: (page: number) => void;
}

/**
 * Moves a 0-indexed page back to the last page once the server reports fewer
 * rows than the page starts at, e.g. after a bulk action moved every row of
 * the last page to another tab. A page that still has rows is left alone.
 *
 * @example
 * useClampPage({
 *   page,
 *   pageSize,
 *   totalCount: isPlaceholderData ? undefined : data?.meta.total_count,
 *   setPage,
 * });
 */
export function useClampPage({
  page,
  pageSize,
  totalCount,
  setPage,
}: ClampPageOptions): void {
  useEffect(() => {
    if (totalCount === undefined || page === 0) {
      return;
    }
    const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [page, pageSize, totalCount, setPage]);
}
